import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent } from '@datadog/browser-rum';

import { isHydrationMessage } from '@jane/bloom/utils/isHydrationError';

/**
 * Final filter of RUM events and errors.
 *
 * When `debug` is enabled (via BLOOM_RUM_DEBUG=true), emits a custom action
 * at each decision point so the filtering behavior can be observed in Datadog
 * RUM Explorer under `@action.target.name:bloom_rum_debug.beforeSend`.
 * @see https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
 */
export const beforeSend =
  ({ debug }: { debug?: boolean }) =>
  (event: RumEvent) => {
    if (event.type !== 'error') return true;

    // Discard known React hydration errors.
    if (isHydrationMessage(event.error.message)) {
      if (debug) {
        datadogRum.addAction('bloom_rum_debug.beforeSend', {
          decision: 'discarded',
          error_message: event.error.message,
          reason: 'hydration_error',
        });
      }
      return false;
    }

    // Discard cross-origin script errors with no actionable details.
    // Production may wrap the message as 'Uncaught "Script error."'.
    if (event.error.message.includes('Script error.')) {
      if (debug) {
        datadogRum.addAction('bloom_rum_debug.beforeSend', {
          decision: 'discarded',
          error_message: event.error.message,
          reason: 'script_error',
        });
      }
      return false;
    }

    // Tag errors that don't originate from the Remix app.
    const origin = event.error.stack?.includes('bloom-assets')
      ? 'bloom-remix'
      : 'unknown';

    if (event.context) {
      event.context['error_origin'] = origin;
    }

    if (debug) {
      datadogRum.addAction('bloom_rum_debug.beforeSend', {
        decision: 'allowed',
        error_message: event.error.message,
        error_origin: origin,
        has_stack: !!event.error.stack,
        reason: 'passed_all_filters',
      });
    }

    return true;
  };
